import { create } from 'zustand';
import type { CreateOrderLineBody, OrderShipToBody } from './api.js';

/**
 * Offline order outbox. When checkout can't reach Spire (no signal / bridge
 * down), the order is queued here with a STABLE idempotency key and retried
 * automatically when connectivity returns — so a rep can place orders in a
 * dead-zone clinic and trust they'll land exactly once.
 */
export interface OutboxOrder {
  /** Stable idempotency key — reused on every retry so Spire dedupes. */
  id: string;
  customerNo: string;
  customerName: string;
  customerPO?: string;
  requiredDate?: string;
  type?: 'order' | 'quote';
  shipTo?: OrderShipToBody;
  notes?: string;
  items: CreateOrderLineBody[];
  createdAt: number;
  lastError?: string;
}

interface OutboxState {
  orders: OutboxOrder[];
  enqueue: (o: OutboxOrder) => void;
  remove: (id: string) => void;
  setError: (id: string, err: string) => void;
}

const KEY = 'kdental:outbox';

function load(): OutboxOrder[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as OutboxOrder[]) : [];
  } catch {
    return [];
  }
}
function persist(orders: OutboxOrder[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(orders));
  } catch {
    /* ignore */
  }
}

export const useOutbox = create<OutboxState>((set, get) => ({
  orders: load(),
  enqueue(o) {
    const orders = [...get().orders.filter((x) => x.id !== o.id), o];
    set({ orders });
    persist(orders);
  },
  remove(id) {
    const orders = get().orders.filter((x) => x.id !== id);
    set({ orders });
    persist(orders);
  },
  setError(id, err) {
    const orders = get().orders.map((x) => (x.id === id ? { ...x, lastError: err } : x));
    set({ orders });
    persist(orders);
  },
}));
